import { signals, storeName, packetLossStatuses, packetLossThresholds } from './constants'
import { videoSessionActions } from 'APP/Store/VideoSession'
import { logger } from 'APP/Lib/Datadog'
import type { Stream } from 'APP/Store/VideoSession/types'
import type { Store } from 'redux'
import type { RootState } from 'APP/Store/CreateStore'

interface Connection {
  connectionId: string
}

interface Session {
  connection?: Connection
  connectionId?: string
}

export interface SignalData {
  sender?: string
  episodeId?: string
}

interface SignalFrom {
  connectionId: string
}

interface SignalEvent {
  type: string
  data: string
  from: SignalFrom
}

interface PublisherStats {
  audioPacketsSent: number
  videoPacketsSent: number
  audioPacketsLost: number
  videoPacketsLost: number
}

interface OpenTokInterface {
  on<T>(event: string, handler: (data: T) => void): void
}

let currentConnectionId: string | null = null

const onConnectionCreatedEventHandler = () => (connection: Connection) => {
  // TODO: emit production logs with scrubbed contents
  logger.debug('Connection created', { connection })
}

const onConnectionDestroyedEventHandler = () => (connection: Connection) => {
  // TODO: emit production logs with scrubbed contents
  logger.debug('Connection destroyed', { connection })
}

const onSessionConnectedEventHandler = (store: Store<RootState>) => (session: Session) => {
  if (!session || !session.connection || !session.connection.connectionId) return
  // TODO: emit production logs with scrubbed contents
  logger.debug('Session connected', { session })
  currentConnectionId = session.connection.connectionId
  store.dispatch(videoSessionActions.sessionConnected())
}

const onSessionDisconnectedEventHandler = (store: Store<RootState>) => (session: Session) => {
  // TODO: emit production logs with scrubbed contents
  logger.debug('Session disconnected', { session })
  currentConnectionId = null
  store.dispatch(videoSessionActions.sessionDisconnected())
}

const onStreamCreatedEventHandler = (store: Store<RootState>) => (stream: Stream) => {
  if (stream.name === signals.properties.senderTypes.CARE_PLATFORM) {
    logger.debug('Platform stream created', { stream })
    store.dispatch(videoSessionActions.platformStreamCreated({ stream }))
  }
}

const onStreamDestroyedEventHandler = (store: Store<RootState>) => (stream: Stream) => {
  if (stream.name === signals.properties.senderTypes.CARE_PLATFORM) {
    // TODO: emit production logs with scrubbed contents
    logger.debug('Platform stream destroyed', { stream })
    store.dispatch(videoSessionActions.platformStreamDestroyed({ stream }))
    store.dispatch(videoSessionActions.resetPublisherStats())
  }
}

const onSessionFailedEventHandler = (store: Store<RootState>) => (error: Error) => {
  // TODO: emit production logs with scrubbed contents
  logger.debug('Session failed', {}, error)
  store.dispatch(videoSessionActions.sessionFailed())
}

const packetLossPercentageToLabel = (percentage: number): keyof typeof packetLossStatuses =>
  (percentage >= packetLossThresholds.CRITICAL && packetLossStatuses.CRITICAL) ||
  (percentage >= packetLossThresholds.WARNING && packetLossStatuses.WARNING) ||
  packetLossStatuses.OKAY

const onPublisherStatsUpdate = (store: Store<RootState>) => (stats: PublisherStats) => {
  logger.debug('publisher stats update', { stats })

  // Extract stats from prev stored event
  const videoSession = store.getState()[storeName]
  const prevStats = videoSession.publisherStats

  // Extract from stats event
  const totalPacketsSent = stats.audioPacketsSent + stats.videoPacketsSent
  const totalPacketsLost = stats.audioPacketsLost + stats.videoPacketsLost

  // Calculate next state
  const currentPacketsSent = totalPacketsSent - prevStats.totalPacketsSent
  const currentPacketsLost = totalPacketsLost - prevStats.totalPacketsLost
  if (currentPacketsSent && currentPacketsLost) {
    const currentLossPercent = (currentPacketsLost / currentPacketsSent) * 100

    // Emit action when loss status changes
    const prevLossStatus = packetLossPercentageToLabel(prevStats.currentLossPercent)
    const currentLossStatus = packetLossPercentageToLabel(currentLossPercent)

    // Store the new metrics
    store.dispatch(
      videoSessionActions.setPublisherStats({
        totalPacketsSent,
        totalPacketsLost,
        currentLossPercent,
      })
    )

    if (prevLossStatus !== currentLossStatus) {
      store.dispatch(
        videoSessionActions.publisherPacketLossEvent({
          currentLossStatus,
        })
      )
    }
  }
}

// Signal Handling
const onSignalEventHandler = (store: Store<RootState>) => (event: SignalEvent) => {
  const signal = event && event
  try {
    signal.data = JSON.parse(signal.data)
    let payload = signal.data as SignalData

    switch (true) {
      // Care Platform Signals
      case isFromCarePlatform(payload) && signal.type === signals.ENDED_CALL:
        logger.info('Received ENDED_CALL signal from the Care Platform', {
          payload: {
            episodeId: payload?.episodeId,
            sender: payload?.sender,
          },
        })
        store.dispatch(videoSessionActions.platformEndedCall())
        break

      case isFromCarePlatform(payload):
        logger.info('Received unknown signal from the Care Platform', {
          payload: {
            episodeId: payload?.episodeId,
            sender: payload?.sender,
          },
        })
        store.dispatch(videoSessionActions.unhandledSignal())
        break

      // Patient Signals
      case isFromPatientApp(payload) && signal.type === signals.ENDED_CALL:
        if (signal.from.connectionId === currentConnectionId) {
          logger.info('Received ENDED_CALL signal from PatientApp with same connectionId', {
            payload: {
              episodeId: payload?.episodeId,
              sender: payload?.sender,
            },
            signalFromConnectionId: signal.from.connectionId,
            currentConnectionId,
          })
        } else {
          logger.info('Received ENDED_CALL signal from PatientApp with different connectionId', {
            payload: {
              episodeId: payload?.episodeId,
              sender: payload?.sender,
            },
            signalFromConnectionId: signal.from.connectionId,
            currentConnectionId,
          })
          logger.info('Call ended by remote')
          store.dispatch(videoSessionActions.handledByRemote())
        }
        break
    }
  } catch (error) {
    store.dispatch(videoSessionActions.malformedSignal())
    logger.error('Failed to handle video session signal', {}, error)
  }
}

const isFromCarePlatform = (payload: SignalData) =>
  payload.sender === signals.properties.senderTypes.CARE_PLATFORM
const isFromPatientApp = (payload: SignalData) =>
  payload.sender === signals.properties.senderTypes.PATIENT

export const attachEventListeners = (
  store: Store<RootState>,
  openTokInterface: OpenTokInterface
) => {
  openTokInterface.on('signal', onSignalEventHandler(store))
  openTokInterface.on('connectionCreated', onConnectionCreatedEventHandler())
  openTokInterface.on('connectionDestroyed', onConnectionDestroyedEventHandler())
  openTokInterface.on('sessionConnected', onSessionConnectedEventHandler(store))
  openTokInterface.on('sessionDisconnected', onSessionDisconnectedEventHandler(store))
  openTokInterface.on('streamCreated', onStreamCreatedEventHandler(store))
  openTokInterface.on('streamDestroyed', onStreamDestroyedEventHandler(store))
  openTokInterface.on('sessionFailed', onSessionFailedEventHandler(store))
  openTokInterface.on('publisherStatsUpdate', onPublisherStatsUpdate(store))
}
